import { SVGS } from '@/assets/svgs'
import { ActionMenu, UserAvatar } from '@/components'
import {
  FollowActionButton,
  RequestActionButton,
  RoundedFilledButton,
  RoundedOutlineButton,
} from '@/components/StyledComponents'
import { useConnectionAdd, useConnectionBlock, useConnectionDelete } from '@/hooks/api/dashboard'
import { useManageConnections } from '@/hooks/api/dashboard/useManageConnections'
import { useAuth, useBrowserQuery, useLocalStorage } from '@/hooks/common'
import { CBox, RBox } from '@/pages/__styled'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import { ConnectionModel, ConnListAction, MyConnection, SearchConnection } from '@/types'
import { getThumbImageURL } from '@/utils/helper'
import styled from '@emotion/styled'
import {
  Block,
  Check,
  Close,
  MoreVert,
  RemoveCircle,
  RoundedCornerOutlined,
  SmartButtonRounded,
} from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'
import { shallow } from 'zustand/shallow'

type ConnectionGridUser = ConnectionModel | MyConnection | SearchConnection

interface Props {
  user: ConnectionGridUser
  onUserAction: (user: ConnectionModel, action: ConnListAction) => void
}

export default function ConnectionGridItem({ user, onUserAction }: Props) {
  const { user: ewUser } = useAuth()
  const navigate = useNavigate()
  const query = useBrowserQuery()
  const {
    acceptConnectionInvite,
    rejectConnectionInvite,
    sendFollowRequest,
    blockConnection,
    removeConnection,
  } = useManageConnections()
  const { pathname } = useLocation()
  const connID = query.get('connID')
  const isSelf = user.userID === ewUser.userID
  const [profile] = useAppStore((state) => [state.user.profile], shallow)

  const showSent = user?.reqSent || false
  const showAccept = !showSent && user?.reqReceived //either false (BOOL) or reqID (String)
  const isFollowing = user.isConnection

  // const blockUser = useConnectionBlock()
  // const removeUser = useConnectionDelete()
  // const [searchConnections] = useAppStore((state) => [state.user.searchConnections], shallow)
  // const { setSearchConnections } = useAppStore()

  /*
  useEffect(() => {
    if (removeUser.isSuccess || blockUser.isSuccess) {
      if (searchConnections[user.userID]) {
        setSearchConnections({
          ...searchConnections,
          [user.userID]: {
            ...searchConnections[user.userID],
            isConnection: false,
          },
        })
      }
    }
  }, [removeUser.isSuccess, blockUser.isSuccess])
  */

  const onClickRemoveConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    toast.promise(removeConnection.mutateAsync(user.userID), {
      loading: 'Removing user...',
      success: <Typography variant='p'>{`${user.firstName} has been removed.`}</Typography>,
      error: <Typography variant='p'>Failed to remove a user.</Typography>,
    })
  }
  const onClickBlockConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    toast.promise(blockConnection.mutateAsync(user.userID), {
      loading: 'Blocking user...',
      success: <Typography variant='p'>{`${user.firstName} has been blocked.`}</Typography>,
      error: <Typography variant='p'>Failed to block a user.</Typography>,
    })
  }

  const onClickConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!sendFollowRequest.isLoading) {
      toast.promise(sendFollowRequest.mutateAsync(user.userID), {
        loading: 'Sending Request...',
        success: <Typography variant='p'>{'Follow request has been sent.'}</Typography>,
        error: <Typography variant='p'>{'Failed to send follow request.'}</Typography>,
      })
    }
  }

  const onClickAcceptRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (typeof user.reqReceived === 'boolean') {
      openUserProfile()
      return
    }

    if (!acceptConnectionInvite.isLoading) {
      toast.promise(
        acceptConnectionInvite.mutateAsync({
          connID: user.userID,
          connReqID: `${user.reqReceived}`,
        }),
        {
          loading: 'Accepting Request...',
          success: <Typography variant='p'>{`${user.firstName} is now a connection`}</Typography>,
          error: <Typography variant='p'>{'Failed to accept follow request.'}</Typography>,
        },
      )
    }
  }

  const onClickDeclineRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (typeof user.reqReceived === 'boolean') {
      openUserProfile()
      return
    }

    if (!rejectConnectionInvite.isLoading) {
      toast.promise(
        rejectConnectionInvite.mutateAsync({
          connID: user.userID,
          connReqID: `${user.reqReceived}`,
        }),
        {
          loading: 'Processing the decline...',
          success: <Typography variant='p'>{`Success!`}</Typography>,
          error: <Typography variant='p'>{'Failed to decline the request.'}</Typography>,
        },
      )
    }
  }

  const topicActions = useMemo(() => {
    return [
      {
        icon: <RemoveCircle />,
        name: 'Unfollow',
        onClick: onClickRemoveConnect,
      },
      {
        icon: <Block />,
        name: 'Block',
        onClick: onClickBlockConnect,
      },
      // {
      //   icon: <SVG src={SVGS.FilterPrivacyConnection} />,
      //   name: 'Shared Groups',
      //   onClick: onClickSharedGroups,
      // },
    ]
  }, [])

  const openUserProfile = () => {
    if (isSelf) {
      navigate(`/profile/me`)
    } else {
      let previewQuery = new URLSearchParams()
      previewQuery.set('id', user.userID)
      navigate(`/profile/user?${previewQuery.toString()}`)
    }
  }

  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent={'center'}
      flexDirection={'column'}
      p={3}
      onClick={openUserProfile}
      sx={{
        cursor: 'pointer',
        width: '280px',
        height: '240px',
        border: `1px solid #f1f5f9`,
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',

        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04)',
          borderColor: '#e2e8f0',
        },
      }}
    >
      <CBox sx={{ alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <UserAvatar
          name={`${user?.firstName} ${user?.lastName}`}
          url={isSelf ? profile?.profilePicURL : getAvatarURL(user)}
          size={72}
          borderRadius={100}
        />

        <CBox
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            textAlign: 'center',
          }}
        >
          <Typography
            fontSize='16px'
            fontWeight={600}
            lineHeight='20px'
            color='#111827'
            fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            letterSpacing='-0.01em'
            className='username'
          >
            {user.firstName} {user.lastName} {isSelf ? '(You)' : ''}
          </Typography>
          {user.profession?.length > 0 && (
            <Typography
              fontSize='14px'
              fontWeight={400}
              lineHeight='18px'
              color='#6b7280'
              fontFamily='Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              sx={{ mt: '2px' }}
            >
              {user.profession}
            </Typography>
          )}
        </CBox>
      </CBox>

      <RBox
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mt: '16px',
          display: isSelf ? 'none' : 'flex',
        }}
      >
        {isFollowing && (
          <FollowActionButton aria-selected onClick={onClickRemoveConnect}>
            <span className='btn-default-text'>{'Following'}</span>
            <span className='btn-hover-text'>{'Unfollow'}</span>
          </FollowActionButton>
        )}
        {!user.isConnection && user.reqReceived && (
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <RequestActionButton kind='reject' onClick={onClickDeclineRequest}>
              Reject
            </RequestActionButton>

            <RequestActionButton kind='accept' onClick={onClickAcceptRequest}>
              Accept
            </RequestActionButton>
          </Box>
        )}
        {!isFollowing && showSent && (
          <FollowActionButton disabled>{'Request Sent'}</FollowActionButton>
        )}
        {!isFollowing && !showSent && !showAccept && (
          <FollowActionButton loading={sendFollowRequest.isLoading} onClick={onClickConnect}>
            {'Follow'}
          </FollowActionButton>
        )}
      </RBox>
    </Box>
  )
}

// Helper
function getAvatarURL(user: ConnectionGridUser): string | undefined {
  if ('profilePicURL' in user && user.profilePicURL) {
    return user.profilePicURL
  }
  if ('thumbImgURL' in user && user.thumbImgURL) {
    return user.thumbImgURL
  }
  if ('albumImgURL' in user && user.albumImgURL) {
    return getThumbImageURL(user.albumImgURL)
  }
  return undefined
}
